import { Badge, Text } from '@tremor/react';
import { fieldLabel } from '../lib/format';

interface Props {
  /** All column names the source emits. Selected ones render at the top in
   *  the chosen order; unselected ones render below to make adding obvious. */
  available: string[];
  /** Ordered list of currently selected columns. Empty array = "all" per the
   *  report builder convention. */
  value: string[];
  onChange: (next: string[]) => void;
}

/**
 * Two-row chip multi-select for picking + ordering report columns. Top row
 * is "selected" — drag-style reorder via up/down nudge buttons (real drag
 * was descoped to keep the dep surface small). Bottom row is "available"
 * — click to add. Empty selection prints a one-liner hint that defaults
 * to "all columns" so the report still runs.
 */
export default function ColumnPicker({ available, value, onChange }: Props) {
  const selected = value;
  const unselected = available.filter((c) => !selected.includes(c));

  function move(idx: number, delta: number) {
    const next = selected.slice();
    const target = idx + delta;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  }
  function remove(idx: number) {
    onChange(selected.filter((_, i) => i !== idx));
  }
  function add(col: string) {
    onChange([...selected, col]);
  }
  function clearAll() {
    onChange([]);
  }

  return (
    <div className="space-y-2">
      <div className="rounded-tremor-default border border-tremor-border p-2 min-h-[44px]">
        {selected.length === 0 ? (
          <Text className="text-tremor-content text-xs italic">
            Sin selección — el reporte mostrará todas las columnas disponibles.
          </Text>
        ) : (
          <div className="flex flex-wrap gap-1">
            {selected.map((col, i) => (
              <span
                key={col}
                className="inline-flex items-center gap-0.5 bg-tremor-brand-faint text-tremor-brand-emphasis rounded-tremor-small px-1.5 py-0.5 text-xs"
                title={col}
              >
                <button
                  className="hover:text-tremor-content-strong disabled:opacity-30"
                  onClick={() => move(i, -1)}
                  disabled={i === 0}
                  aria-label="mover izquierda"
                  type="button"
                >‹</button>
                <span className="font-medium">{fieldLabel(col)}</span>
                <button
                  className="hover:text-tremor-content-strong disabled:opacity-30"
                  onClick={() => move(i, +1)}
                  disabled={i === selected.length - 1}
                  aria-label="mover derecha"
                  type="button"
                >›</button>
                <button
                  className="ml-0.5 hover:text-red-600 dark:hover:text-red-400"
                  onClick={() => remove(i)}
                  aria-label="quitar"
                  type="button"
                >×</button>
              </span>
            ))}
          </div>
        )}
      </div>

      {unselected.length > 0 && (
        <div>
          <div className="flex items-baseline gap-2 mb-1">
            <Text className="text-tremor-content text-xs">Disponibles (clic para agregar)</Text>
            {selected.length > 0 && (
              <button
                type="button"
                onClick={clearAll}
                className="text-tremor-content text-xs underline hover:text-tremor-content-strong ml-auto"
              >
                quitar todas
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-1">
            {unselected.map((col) => (
              <button
                key={col}
                type="button"
                onClick={() => add(col)}
                className="text-xs"
                title={col}
              >
                <Badge color="gray">+ {fieldLabel(col)}</Badge>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
